import { FC, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiArrowLeft, FiCompass, FiRefreshCw } from 'react-icons/fi';
import { RootState } from '@/store';
import { usePageMeta } from '@/hooks/usePageMeta';
import { pageTitle } from '@/utils/documentTitle';

interface NotFound404Props {
    /** Set when a profile URL did not resolve, so the copy can name it. */
    username?: string;
    /** `error` is for a failed load (network or server), which is not the same as "not found". */
    variant?: 'not-found' | 'error';
    onRetry?: () => void;
}

const NOT_FOUND_META = {
    documentTitle: pageTitle('Page Not Found'),
    metaDescription: 'This page is not on JamWith. Find musicians and collaborators to jam with.',
    robots: 'noindex, follow',
};

const ERROR_META = {
    documentTitle: pageTitle('Something Went Wrong'),
    robots: 'noindex, follow',
};

// hover:!text-white beats the global `a:hover { color: unset !important }` so the label stays white.
const PRIMARY_LINK_CLASS = 'inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-lightpurple-purple-horiz px-6 font-bold text-white no-underline shadow-lg shadow-purple-light/25 transition hover:!text-white hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-light';
const SECONDARY_BUTTON_CLASS = 'inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-6 font-bold text-gray-700 no-underline transition hover:border-purple-light hover:text-purple-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-light';

/** The record in the middle of "404". It spins slowly unless the visitor prefers reduced motion. */
const Vinyl: FC = () => (
    <svg
        viewBox="0 0 100 100"
        className="h-[1em] w-[1em] motion-safe:animate-[spin_8s_linear_infinite]"
        aria-hidden
        focusable="false"
    >
        <defs>
            <linearGradient id="nf-vinyl" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#4E45C8" />
                <stop offset="100%" stopColor="#262262" />
            </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="48" fill="url(#nf-vinyl)" />
        <circle cx="50" cy="50" r="38" fill="none" stroke="#fff" strokeOpacity="0.18" strokeWidth="1.5" />
        <circle cx="50" cy="50" r="30" fill="none" stroke="#fff" strokeOpacity="0.14" strokeWidth="1.5" />
        <circle cx="50" cy="50" r="22" fill="none" stroke="#fff" strokeOpacity="0.1" strokeWidth="1.5" />
        <path d="M50 10a40 40 0 0 1 28 12" fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="3" strokeLinecap="round" />
        <circle cx="50" cy="50" r="14" fill="#ef3e61" />
        <circle cx="50" cy="50" r="3" fill="#fff" />
    </svg>
);

const NotFound404: FC<NotFound404Props> = ({ username, variant = 'not-found', onRetry }) => {
    const user = useSelector((state: RootState) => state.auth.user);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const isError = variant === 'error';
    // react-router stores its position in history state; 0 means this tab opened on the page.
    const canGoBack = (window.history.state?.idx ?? 0) > 0;

    usePageMeta(isError ? ERROR_META : NOT_FOUND_META);

    // Route changes do not move focus, so screen reader and keyboard users would stay on the old page.
    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    const title = isError ? "We couldn't load this page" : 'This page is off the setlist';
    const description = isError
        ? 'Something went wrong on our end, or your connection dropped. Give it another try.'
        : username
            ? `Nobody on JamWith goes by @${username}. The username may have changed, or the account may no longer exist.`
            : "The link may be broken or the page may have moved. Let's get you back to the music.";

    return (
        <section className="flex min-h-[calc(100vh-140px)] items-center justify-center px-4 pb-16">
            <div className="flex w-full max-w-xl flex-col items-center text-center">
                {!isError && (
                    <div
                        className="flex select-none items-center justify-center text-[clamp(5rem,22vw,9rem)] font-extrabold leading-none tracking-tight text-purple-light"
                        aria-hidden
                    >
                        <span>4</span>
                        <Vinyl />
                        <span>4</span>
                    </div>
                )}

                <h1
                    ref={headingRef}
                    tabIndex={-1}
                    className="mt-6 text-2xl font-extrabold leading-tight text-gray-900 outline-none sm:text-[32px]"
                >
                    {title}
                </h1>
                <p className="mt-3 max-w-md text-base text-gray-500">{description}</p>

                {!isError && !username && pathname !== '/404' && (
                    <p
                        className="mt-4 max-w-full truncate rounded-lg bg-white px-3 py-1.5 font-mono text-sm text-gray-500 shadow-sm"
                        title={pathname}
                    >
                        {pathname}
                    </p>
                )}

                <div className="mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row">
                    {isError && onRetry ? (
                        <button type="button" onClick={onRetry} className={`${PRIMARY_LINK_CLASS} border-0`}>
                            <FiRefreshCw aria-hidden /> Try again
                        </button>
                    ) : (
                        <Link to={user ? '/discover' : '/directory'} className={PRIMARY_LINK_CLASS}>
                            <FiCompass aria-hidden /> {user ? 'Discover musicians' : 'Browse musicians'}
                        </Link>
                    )}
                    {canGoBack ? (
                        <button type="button" onClick={() => navigate(-1)} className={SECONDARY_BUTTON_CLASS}>
                            <FiArrowLeft aria-hidden /> Go back
                        </button>
                    ) : !user ? (
                        <Link to="/login" className={SECONDARY_BUTTON_CLASS}>
                            Sign in
                        </Link>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default NotFound404;
